"use client";

import { useRef, useState } from "react";
import {
  platform,
  type DesignWebsite,
  type DesignWebsiteCreateInput,
} from "./api";
import { Button, Input } from "./ui";
import css from "./BuildyWorkspace.module.css";

export function WebsiteCreation({
  org,
  csrf,
  onCreated,
}: {
  org: number;
  csrf: string;
  onCreated: (website: DesignWebsite) => void;
}) {
  const storageKey = `buildy:website-creation:${org}`;
  const [recovery] = useState<DesignWebsiteCreateInput | null>(() => {
    try {
      const value = JSON.parse(sessionStorage.getItem(storageKey) ?? "null");
      return value &&
        typeof value.name === "string" &&
        typeof value.request_key === "string"
        ? value
        : null;
    } catch {
      return null;
    }
  });
  const [name, setName] = useState(recovery?.name ?? "");
  const [url, setUrl] = useState(recovery?.source_url ?? "");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [uncertain, setUncertain] = useState(Boolean(recovery));
  const request = useRef<DesignWebsiteCreateInput | null>(recovery);
  const inFlight = useRef(false);
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (inFlight.current || !name.trim()) return;
        const body = request.current ?? {
          name: name.trim(),
          source_url: url.trim(),
          request_key: crypto.randomUUID(),
        };
        request.current = body;
        try {
          sessionStorage.setItem(storageKey, JSON.stringify(body));
        } catch {
          /* Retry remains stable while open. */
        }
        inFlight.current = true;
        setPending(true);
        setError("");
        void platform<DesignWebsite>("websites/", {
          method: "POST",
          org,
          csrf,
          body,
        })
          .then((website) => {
            try {
              sessionStorage.removeItem(storageKey);
            } catch {
              /* Optional recovery storage. */
            }
            onCreated(website);
          })
          .catch((cause: unknown) => {
            const status = (cause as { status?: number })?.status;
            const unknown = !status || status >= 500 || status === 409;
            setUncertain(unknown);
            if (!unknown) {
              request.current = null;
              try {
                sessionStorage.removeItem(storageKey);
              } catch {
                /* Optional recovery storage. */
              }
            }
            setError(
              cause instanceof Error
                ? cause.message
                : "The website could not be created.",
            );
          })
          .finally(() => {
            inFlight.current = false;
            setPending(false);
          });
      }}
    >
      <p>Start with the business and its current website.</p>
      <label>
        Website name
        <Input
          required
          maxLength={160}
          autoComplete="organization"
          placeholder="Business name"
          value={name}
          disabled={pending || uncertain}
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      <label>
        Website address (optional)
        <Input
          type="url"
          placeholder="https://example.com"
          value={url}
          disabled={pending || uncertain}
          onChange={(event) => setUrl(event.target.value)}
        />
      </label>
      {uncertain && (
        <p className={css.setupNotice}>
          Check the previous request before starting another website.
        </p>
      )}
      {error && <p role="alert">{error}</p>}
      <Button
        type="submit"
        variant="primary"
        disabled={pending || !name.trim()}
      >
        {pending
          ? "Creating…"
          : uncertain
            ? "Check previous request"
            : "Create website"}
      </Button>
    </form>
  );
}
